import { z } from 'zod';
import {
  type SendChatMessageType,
  type SendGroupChatMessageType,
  SendMessageCommand,
} from '@/models/messages/send-message';
import type { UserInfoType } from '@/models/user/user-info';
import { isGroupMessage } from '@/utils/message';
import { avatarUrlSchema, nameSchema, stringifiedDateSchema } from '@/utils/schemas';

export enum ConversationMessageContentType {
  Text = 'txt',
  Emoticon = 'emo',
  Image = 'pic',
  Audio = 'audio',
  Video = 'video',
  Contact = 'card',
  Notice = 'notice',
  AllBan = 'noticeallban',
  File = 'file',
  Call = 'call',
  Invite = 'invite',
  JoinGroup = 'GroupJoin',
  LeaveGroup = 'GroupLeave',
}

const commonConversationMessageScheme = z.object({
  Status: z.union([z.number(), z.boolean()]).pipe(z.coerce.boolean()).nullable(),
  ID: z.number(),
  UserID: z.number(),
  UserNickName: nameSchema,
  UserDisplayName: z.string().nullish(),
  UserHeadImg: avatarUrlSchema,
  Message: z.string(),
  MsgType: z.nativeEnum(ConversationMessageContentType),
  TimeStamp: z.number().int(),
  IsRead: z.number().or(z.boolean()).pipe(z.coerce.boolean()).default(false).optional(),
  ReadDelete: z.number().or(z.boolean()).pipe(z.coerce.boolean()).default(false).optional(),
  AutoDeleteTime: z.number().optional(),
  Quote: z.string().optional(),
  ReadStamp: z.number().int().optional(),
  SeqID: z.number().int().optional().default(0),
  ClientID: z.string().optional(),
  CreateTime: stringifiedDateSchema,
  RetrieveTime: z.number().int().optional().default(Date.now()),
});

export const conversationMessageScheme = commonConversationMessageScheme.extend({
  ChatSessionID: z.number(),
});

export const conversationGroupMessageScheme = commonConversationMessageScheme.extend({
  GroupID: z.number(),
  UserRole: z.number().optional(),
  AtIDs: z.string().nullable().optional(),
  GotAt: z.boolean().default(false).optional(),
  GroupInfo: z.string().nullish(),
});

export const conversationMessageUnionScheme = z.union([conversationMessageScheme, conversationGroupMessageScheme]);

export type ConversationMessageType = z.infer<typeof conversationMessageScheme>;

export type ConversationGroupMessageType = z.infer<typeof conversationGroupMessageScheme>;

export type ConversationMessageTypeUnion = ConversationMessageType | ConversationGroupMessageType;

export function transformSendMessageToConversationMessage(
  message: SendChatMessageType | SendGroupChatMessageType,
  userInfo?: UserInfoType,
): ConversationMessageTypeUnion {
  switch (message.Cmd) {
    case SendMessageCommand.Chat:
      return {
        Status: null,
        ID: message.TimeStamp,
        ChatSessionID: message.ChatSessionID,
        UserID: message.UserID,
        UserNickName: userInfo?.NickName ?? '',
        UserHeadImg: userInfo?.HeadImg ?? '',
        Message: message.Message,
        MsgType: message.MsgType,
        CreateTime: message.TimeStamp + 1000,
        RetrieveTime: message.TimeStamp + 1000,
        TimeStamp: message.TimeStamp,
        ClientID: message.ClientMsgId,
        IsRead: false,
        ReadStamp: message.TimeStamp,
        SeqID: -1,
      } satisfies ConversationMessageType;
    case SendMessageCommand.GroupChat:
      return {
        Status: null,
        ID: message.TimeStamp,
        GroupID: message.GroupID,
        UserID: message.UserID,
        UserNickName: userInfo?.NickName ?? '',
        UserHeadImg: userInfo?.HeadImg ?? '',
        Message: message.Message,
        MsgType: message.MsgType,
        CreateTime: message.TimeStamp + 1000,
        RetrieveTime: message.TimeStamp + 1000,
        TimeStamp: message.TimeStamp,
        ClientID: message.ClientMsgId,
        IsRead: true,
        ReadStamp: message.TimeStamp,
        AtIDs: message.AtIDs,
        GotAt: false,
        SeqID: -1,
      } satisfies ConversationGroupMessageType;
  }
}

export function transformConversationMessageToSendMessage(conversation: ConversationMessageTypeUnion) {
  if (isGroupMessage(conversation)) {
    return {
      Cmd: SendMessageCommand.GroupChat,
      MsgType: conversation.MsgType,
      TimeStamp: conversation.TimeStamp,
      ClientMsgId: conversation.ClientID,
      GroupID: conversation.GroupID,
      UserID: conversation.UserID,
      Message: conversation.Message,
      AtIDs: conversation.AtIDs ?? null,
    } satisfies SendGroupChatMessageType;
  }

  return {
    Cmd: SendMessageCommand.Chat,
    MsgType: conversation.MsgType,
    TimeStamp: conversation.TimeStamp,
    ClientMsgId: conversation.ClientID,
    ChatSessionID: conversation.ChatSessionID,
    UserID: conversation.UserID,
    Message: conversation.Message,
  } satisfies SendChatMessageType;
}
